import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { ArrowRight, Upload } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface PersonalInfo {
  fullName: string;
  email: string;
  phone: string;
  nationality: string;
  currentLocation: string;
  graduationYear: string;
  university: string;
  degree: string;
  fieldOfStudy: string;
  linkedinUrl: string;
  portfolioUrl: string;
}

interface Assessment {
  careerGoals: string;
  learningExample: string;
  problemSolving: string;
  leadershipExample: string;
  communicationExample: string;
  initiativeExample: string;
  collaborationExample: string;
  resilienceExample: string;
  developmentAreas: string;
  achievements: string;
}

const HighPotentialForm = () => {
  const { toast } = useToast();
  const [personalInfo, setPersonalInfo] = useState<PersonalInfo>({
    fullName: "",
    email: "",
    phone: "",
    nationality: "",
    currentLocation: "",
    graduationYear: "",
    university: "",
    degree: "",
    fieldOfStudy: "",
    linkedinUrl: "",
    portfolioUrl: "",
  });

  const [assessment, setAssessment] = useState<Assessment>({
    careerGoals: "",
    learningExample: "",
    problemSolving: "",
    leadershipExample: "",
    communicationExample: "",
    initiativeExample: "",
    collaborationExample: "",
    resilienceExample: "",
    developmentAreas: "",
    achievements: "",
  });

  const handleSubmit = () => {
    // Validate required fields
    const requiredFields = [
      'fullName', 'email', 'phone', 'university', 
      'degree', 'graduationYear', 'fieldOfStudy'
    ];
    
    const missingFields = requiredFields.filter(field => !personalInfo[field]);
    
    if (missingFields.length > 0) {
      toast({
        variant: "destructive",
        title: "Missing Information",
        description: "Please fill in all required personal information fields.",
      });
      return;
    }

    // Process form data
    const formData = {
      personalInfo,
      assessment,
    };

    console.log("Form submission:", formData);
    
    toast({
      title: "Assessment Submitted",
      description: "Your High Potential Individual assessment has been submitted successfully.",
    });
  };

  return (
    <Card className="w-full max-w-4xl mx-auto">
      <CardHeader>
        <CardTitle>High Potential Individual Assessment</CardTitle>
      </CardHeader>
      <CardContent className="space-y-8">
        {/* CV Upload Section */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold">1. CV Upload</h3>
          <div
            className="border-2 border-dashed rounded-lg p-6 text-center cursor-pointer hover:border-primary/50 transition-colors"
            onClick={() => document.getElementById('cv-upload')?.click()}
          >
            <input
              id="cv-upload"
              type="file"
              className="hidden"
              accept=".pdf,.doc,.docx"
            />
            <Upload className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
            <p className="text-sm text-muted-foreground">
              Upload your CV highlighting key achievements and experiences
            </p>
          </div>
        </div>

        {/* Personal Information */}
        <div className="space-y-6">
          <h3 className="text-lg font-semibold">2. Personal Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="fullName">Full Name *</Label>
              <Input
                id="fullName"
                value={personalInfo.fullName}
                onChange={(e) => setPersonalInfo(prev => ({ ...prev, fullName: e.target.value }))}
                placeholder="Enter your full name"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email Address *</Label>
              <Input
                id="email"
                type="email"
                value={personalInfo.email}
                onChange={(e) => setPersonalInfo(prev => ({ ...prev, email: e.target.value }))}
                placeholder="Enter your email"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone">Phone Number *</Label>
              <Input
                id="phone"
                value={personalInfo.phone}
                onChange={(e) => setPersonalInfo(prev => ({ ...prev, phone: e.target.value }))}
                placeholder="Enter your phone number"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="nationality">Nationality *</Label>
              <Input
                id="nationality"
                value={personalInfo.nationality}
                onChange={(e) => setPersonalInfo(prev => ({ ...prev, nationality: e.target.value }))}
                placeholder="Enter your nationality"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="university">University *</Label>
              <Input
                id="university"
                value={personalInfo.university}
                onChange={(e) => setPersonalInfo(prev => ({ ...prev, university: e.target.value }))}
                placeholder="Enter your university name"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="degree">Degree Level *</Label>
              <Select
                value={personalInfo.degree}
                onValueChange={(value) => setPersonalInfo(prev => ({ ...prev, degree: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select degree level" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="bachelors">Bachelor's Degree</SelectItem>
                  <SelectItem value="masters">Master's Degree</SelectItem>
                  <SelectItem value="phd">PhD</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="fieldOfStudy">Field of Study *</Label>
              <Input
                id="fieldOfStudy"
                value={personalInfo.fieldOfStudy}
                onChange={(e) => setPersonalInfo(prev => ({ ...prev, fieldOfStudy: e.target.value }))}
                placeholder="Enter your field of study"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="graduationYear">Graduation Year *</Label>
              <Input
                id="graduationYear"
                type="number"
                min="2000"
                max="2025"
                value={personalInfo.graduationYear}
                onChange={(e) => setPersonalInfo(prev => ({ ...prev, graduationYear: e.target.value }))}
                placeholder="Enter graduation year"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="linkedinUrl">LinkedIn Profile</Label>
              <Input
                id="linkedinUrl"
                type="url"
                value={personalInfo.linkedinUrl}
                onChange={(e) => setPersonalInfo(prev => ({ ...prev, linkedinUrl: e.target.value }))}
                placeholder="Enter your LinkedIn URL"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="portfolioUrl">Portfolio/Website</Label>
              <Input
                id="portfolioUrl"
                type="url"
                value={personalInfo.portfolioUrl}
                onChange={(e) => setPersonalInfo(prev => ({ ...prev, portfolioUrl: e.target.value }))}
                placeholder="Enter your portfolio URL"
              />
            </div>
          </div>
        </div>

        {/* Assessment Questions */}
        <div className="space-y-6 border-t pt-6">
          <h3 className="text-lg font-semibold">3. Assessment Questions</h3>
          
          <div className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="careerGoals">Career Aspirations and Motivation</Label>
              <p className="text-sm text-muted-foreground">What are your long-term career goals, and how do you plan to achieve them?</p>
              <Textarea
                id="careerGoals"
                value={assessment.careerGoals}
                onChange={(e) => setAssessment(prev => ({ ...prev, careerGoals: e.target.value }))}
                placeholder="Describe your career goals and plans"
                className="min-h-[100px]"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="learningExample">Learning Agility</Label>
              <p className="text-sm text-muted-foreground">Describe a situation where you had to quickly learn or adapt to something new.</p>
              <Textarea
                id="learningExample"
                value={assessment.learningExample}
                onChange={(e) => setAssessment(prev => ({ ...prev, learningExample: e.target.value }))}
                placeholder="Share your experience"
                className="min-h-[100px]"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="problemSolving">Strategic Thinking and Problem-Solving</Label>
              <p className="text-sm text-muted-foreground">Tell us about a complex challenge you faced and how you approached it.</p>
              <Textarea
                id="problemSolving"
                value={assessment.problemSolving}
                onChange={(e) => setAssessment(prev => ({ ...prev, problemSolving: e.target.value }))}
                placeholder="Describe the challenge and your solution"
                className="min-h-[100px]"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="leadershipExample">Influence and Leadership</Label>
              <p className="text-sm text-muted-foreground">Share an example of when you led a team or influenced others toward a goal.</p>
              <Textarea
                id="leadershipExample"
                value={assessment.leadershipExample}
                onChange={(e) => setAssessment(prev => ({ ...prev, leadershipExample: e.target.value }))}
                placeholder="Describe your leadership experience"
                className="min-h-[100px]"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="communicationExample">Communication and Emotional Intelligence</Label>
              <p className="text-sm text-muted-foreground">Describe a situation when you had to communicate a difficult message or resolve a conflict.</p>
              <Textarea
                id="communicationExample"
                value={assessment.communicationExample}
                onChange={(e) => setAssessment(prev => ({ ...prev, communicationExample: e.target.value }))}
                placeholder="Share your experience"
                className="min-h-[100px]"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="initiativeExample">Initiative and Resourcefulness</Label>
              <p className="text-sm text-muted-foreground">Give an example of when you took initiative beyond your normal responsibilities.</p>
              <Textarea
                id="initiativeExample"
                value={assessment.initiativeExample}
                onChange={(e) => setAssessment(prev => ({ ...prev, initiativeExample: e.target.value }))}
                placeholder="Describe your initiative"
                className="min-h-[100px]"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="collaborationExample">Collaboration and Teamwork</Label>
              <p className="text-sm text-muted-foreground">Talk about a time you worked with individuals from diverse backgrounds or expertise.</p>
              <Textarea
                id="collaborationExample"
                value={assessment.collaborationExample}
                onChange={(e) => setAssessment(prev => ({ ...prev, collaborationExample: e.target.value }))}
                placeholder="Share your collaboration experience"
                className="min-h-[100px]"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="resilienceExample">Resilience and Adaptability</Label>
              <p className="text-sm text-muted-foreground">Share a time you encountered a major setback or unexpected challenge.</p>
              <Textarea
                id="resilienceExample"
                value={assessment.resilienceExample}
                onChange={(e) => setAssessment(prev => ({ ...prev, resilienceExample: e.target.value }))}
                placeholder="Describe how you handled the challenge"
                className="min-h-[100px]"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="developmentAreas">Self-Awareness and Development</Label>
              <p className="text-sm text-muted-foreground">What are your biggest development areas or weaknesses, and how have you worked to address them?</p>
              <Textarea
                id="developmentAreas"
                value={assessment.developmentAreas}
                onChange={(e) => setAssessment(prev => ({ ...prev, developmentAreas: e.target.value }))}
                placeholder="Describe your development areas"
                className="min-h-[100px]"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="achievements">Results Orientation</Label>
              <p className="text-sm text-muted-foreground">Describe a goal you pursued that required significant effort and the measurable results you achieved.</p>
              <Textarea
                id="achievements"
                value={assessment.achievements}
                onChange={(e) => setAssessment(prev => ({ ...prev, achievements: e.target.value }))}
                placeholder="Share your achievements"
                className="min-h-[100px]"
              />
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <Button 
          onClick={handleSubmit}
          className="w-full"
          size="lg"
        >
          Submit Assessment
          <ArrowRight className="ml-2 h-5 w-5" />
        </Button>
      </CardContent>
    </Card>
  );
};

export default HighPotentialForm;